import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Stack } from "@mui/material";
import {
  PerformancePeriod,
  setPerformanceData,
  setPerformanceLoading,
  setPerformanceMonthlyData,
  setPerformanceMonthlyLoading,
  setPerformanceWeeklyData,
  setPerformanceWeeklyLoading,
  TableSort,
} from "../../../../../features/optifundDashboardSlice";
import { OptifundDashboardQueryIds } from "../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import { CsvSection, sortRowsForExport } from "../csvExport";
import { useOptifundTabData } from "../hooks/useOptifundTabData";
import { getDefaultPerformanceDateRange } from "./performanceDateDefaults";
import {
  mapPerformanceApiRow,
  PERFORMANCE_CSV_HEADERS,
  PerformanceRow,
  toPerformanceCsvRow,
} from "./performanceApiMapping";
import PerformanceKpiCards from "./PerformanceKpiCards";
import PerformanceCharts from "./PerformanceCharts";
import PerformanceTable, { PERIOD_LABELS } from "./PerformanceTable";
import PerformanceSkeleton from "./PerformanceSkeleton";
import PerformanceTableSkeleton from "./PerformanceTableSkeleton";

const EMPTY_ROWS: PerformanceRow[] = [];
const DEFAULT_SORT: TableSort = { keytoSort: "date", direction: "desc" };
const PAGE_SIZE_OPTIONS = [10, 25, 100];

interface PerformanceProps {
  userToken: any;
  websiteSharedUid: string;
}

export interface PerformanceExportHandle {
  getExportSections: () => CsvSection[] | null;
}

function isPerformancePeriod(value: string | null): value is PerformancePeriod {
  return value === "daily" || value === "weekly" || value === "monthly";
}

const Performance = forwardRef<PerformanceExportHandle, PerformanceProps>(function Performance(
  { userToken, websiteSharedUid },
  ref
) {
  const dispatch = useDispatch();
  const {
    performanceData,
    performanceLoading,
    performanceWeeklyData,
    performanceWeeklyLoading,
    performanceMonthlyData,
    performanceMonthlyLoading,
  } = useSelector((state: any) => state.optifundDashboard);

  // Filter/sort/pagination state for this tab lives here (not Redux) and is
  // synced with the URL directly via useSearchParams - see
  // docs/new-module-checklist.md Section 5/8.12, and useLeadsManagementData.ts
  // for the established reference pattern this mirrors.
  const [searchParams, setSearchParams] = useSearchParams();

  const [period, setPeriod] = useState<PerformancePeriod>(() => {
    const value = searchParams.get("perf_period");
    return isPerformancePeriod(value) ? value : "daily";
  });
  const [tablePage, setTablePage] = useState(() => {
    const value = Number(searchParams.get("perf_page"));
    return Number.isInteger(value) && value >= 0 ? value : 0;
  });
  const [tableRowsPerPage, setTableRowsPerPage] = useState(() => {
    const value = Number(searchParams.get("perf_page_size"));
    return PAGE_SIZE_OPTIONS.includes(value) ? value : 10;
  });
  const [tableSort, setTableSort] = useState<TableSort>(() => {
    const key = searchParams.get("perf_sort");
    const direction = searchParams.get("perf_dir");
    return key && (direction === "asc" || direction === "desc")
      ? { keytoSort: key, direction }
      : DEFAULT_SORT;
  });

  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("perf_start");
        next.delete("perf_end");
        if (period === "daily") next.delete("perf_period");
        else next.set("perf_period", period);
        if (tablePage === 0) next.delete("perf_page");
        else next.set("perf_page", String(tablePage));
        if (tableRowsPerPage === 10) next.delete("perf_page_size");
        else next.set("perf_page_size", String(tableRowsPerPage));
        if (tableSort.keytoSort === DEFAULT_SORT.keytoSort && tableSort.direction === DEFAULT_SORT.direction) {
          next.delete("perf_sort");
          next.delete("perf_dir");
        } else {
          next.set("perf_sort", tableSort.keytoSort);
          next.set("perf_dir", tableSort.direction);
        }
        return next;
      },
      { replace: true }
    );
  }, [period, tablePage, tableRowsPerPage, tableSort, setSearchParams]);

  const handlePeriodChange = (nextPeriod: PerformancePeriod) => {
    setPeriod(nextPeriod);
    setTablePage(0);
  };

  const handleRowsPerPageChange = (nextRowsPerPage: number) => {
    setTableRowsPerPage(nextRowsPerPage);
    setTablePage(0);
  };

  // start_date/end_date remain required on every Performance query. The
  // UI has no date controls, so each query always uses its default period.
  const [dailyStart, dailyEnd] = getDefaultPerformanceDateRange("daily");
  const [weeklyStart, weeklyEnd] = getDefaultPerformanceDateRange("weekly");
  const [monthlyStart, monthlyEnd] = getDefaultPerformanceDateRange("monthly");

  // Daily always fetches (it feeds the KPI cards/charts regardless of
  // which period is selected in the Account Summary table below). Weekly
  // and Monthly only fetch once their period button is actually selected -
  // true lazy-load per period - and each stores its result in Redux
  // independently.
  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.performanceDailyId,
    (data) => dispatch(setPerformanceData(data)),
    (loading) => dispatch(setPerformanceLoading(loading)),
    dailyStart.format("YYYY-MM-DD"),
    dailyEnd.format("YYYY-MM-DD")
  );

  useOptifundTabData(
    userToken,
    period === "weekly" ? websiteSharedUid : null,
    OptifundDashboardQueryIds.performanceWeeklyId,
    (data) => dispatch(setPerformanceWeeklyData(data)),
    (loading) => dispatch(setPerformanceWeeklyLoading(loading)),
    weeklyStart.format("YYYY-MM-DD"),
    weeklyEnd.format("YYYY-MM-DD")
  );

  useOptifundTabData(
    userToken,
    period === "monthly" ? websiteSharedUid : null,
    OptifundDashboardQueryIds.performanceMonthlyId,
    (data) => dispatch(setPerformanceMonthlyData(data)),
    (loading) => dispatch(setPerformanceMonthlyLoading(loading)),
    monthlyStart.format("YYYY-MM-DD"),
    monthlyEnd.format("YYYY-MM-DD")
  );

  const dailyRows: PerformanceRow[] = useMemo(
    () => (Array.isArray(performanceData) ? performanceData.map(mapPerformanceApiRow) : EMPTY_ROWS),
    [performanceData]
  );
  const weeklyRows: PerformanceRow[] = useMemo(
    () => (Array.isArray(performanceWeeklyData) ? performanceWeeklyData.map(mapPerformanceApiRow) : EMPTY_ROWS),
    [performanceWeeklyData]
  );
  const monthlyRows: PerformanceRow[] = useMemo(
    () => (Array.isArray(performanceMonthlyData) ? performanceMonthlyData.map(mapPerformanceApiRow) : EMPTY_ROWS),
    [performanceMonthlyData]
  );

  const rowsByPeriod: Record<string, PerformanceRow[]> = useMemo(
    () => ({
      daily: dailyRows,
      weekly: weeklyRows,
      monthly: monthlyRows,
    }),
    [dailyRows, weeklyRows, monthlyRows]
  );

  // "Loading" here means "don't have this period's real rows yet" as well
  // as "actively fetching" - that way there's never a frame where a period
  // renders with another period's (or no) data before its own fetch has
  // actually resolved.
  const isDailyLoading = performanceLoading || !Array.isArray(performanceData);
  const isSelectedPeriodLoading =
    period === "weekly"
      ? performanceWeeklyLoading || !Array.isArray(performanceWeeklyData)
      : period === "monthly"
      ? performanceMonthlyLoading || !Array.isArray(performanceMonthlyData)
      : isDailyLoading;

  useImperativeHandle(
    ref,
    () => ({
      getExportSections: (): CsvSection[] | null => {
        if (isSelectedPeriodLoading) return null;
        return [
          {
            title: `Performance - ${PERIOD_LABELS[period]}`,
            headers: PERFORMANCE_CSV_HEADERS,
            rows: sortRowsForExport(rowsByPeriod[period], tableSort).map(toPerformanceCsvRow),
          },
        ];
      },
    }),
    [isSelectedPeriodLoading, period, rowsByPeriod, tableSort]
  );

  // KPI cards/charts are always Daily-based regardless of which period is
  // toggled in the Account Summary table below, so nothing on this tab can
  // render until Daily's own fetch resolves.
  if (isDailyLoading) {
    return <PerformanceSkeleton />;
  }

  return (
    <Stack spacing={2.5}>
      <PerformanceKpiCards rows={dailyRows} />
      <PerformanceCharts rows={dailyRows} />
      {isSelectedPeriodLoading ? (
        <PerformanceTableSkeleton />
      ) : (
        <PerformanceTable
          rows={rowsByPeriod[period]}
          period={period}
          onPeriodChange={handlePeriodChange}
          page={tablePage}
          onPageChange={setTablePage}
          rowsPerPage={tableRowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          sort={tableSort}
          onSortChange={setTableSort}
        />
      )}
    </Stack>
  );
});

export default Performance;
